/**
 * TrustPage — public, shareable compliance dashboard for one site.
 *
 * Lives at `/trust/:siteId` (outside <PrivateRoute>). Restaurants can
 * paste the link into their menu, Google Business profile, or Instagram
 * bio so customers see a live "this kitchen is logging cold-chain
 * checks every day" badge. Auditors get the same view as a quick proof
 * that compliance culture is real, not just at-audit-time paperwork.
 *
 * Privacy guardrails (mirrors the backend):
 *   - No raw temperature values
 *   - No equipment names that reveal supplier/menu info
 *   - No staff names
 *   - No notes / corrective-action text
 *   - Only counts and percentages
 *
 * Each site must explicitly opt-in via Settings → "Publish public trust
 * page". Default OFF.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import PublicShell from "@/components/PublicShell";
import { Badge } from "@/components/ui/badge";
import { Snowflake, ShieldCheck, AlertTriangle, Sparkles, GraduationCap, Clock, CheckCircle2 } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); } catch { return iso; }
}

/** Tailwind-class triple keyed by score band so the gauge and grade
 *  badge agree at a glance. */
function gradeStyles(score) {
  if (score === null || score === undefined) return { ring: "text-charcoal-soft", chip: "bg-stone-100 text-charcoal-soft" };
  if (score >= 95) return { ring: "text-eucalyptus-700", chip: "bg-eucalyptus-100 text-eucalyptus-900" };
  if (score >= 85) return { ring: "text-amber-600", chip: "bg-amber-100 text-amber-900" };
  return { ring: "text-ochre-600", chip: "bg-ochre-100 text-ochre-900" };
}

/** Inline conic-gradient gauge — pure CSS, no canvas/svg lib needed. */
function TrustGauge({ score }) {
  const pct = score ?? 0;
  const angle = Math.round((pct / 100) * 360);
  const ring = score === null
    ? "var(--charcoal-soft, #6B6B6B)"
    : score >= 95 ? "rgb(58,90,64)"
    : score >= 85 ? "rgb(217,119,6)"
    : "rgb(190,75,46)";
  return (
    <div
      className="relative w-40 h-40 md:w-48 md:h-48 rounded-full"
      data-testid="trust-gauge"
      style={{
        background: `conic-gradient(${ring} ${angle}deg, rgba(0,0,0,0.06) ${angle}deg)`,
      }}
    >
      <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
        {score === null ? (
          <>
            <div className="text-3xl font-display font-extrabold text-charcoal-soft">—</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-soft mt-1">No data yet</div>
          </>
        ) : (
          <>
            <div className="text-4xl md:text-5xl font-display font-extrabold leading-none">
              {score}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-soft mt-1">
              Trust score
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** 30-day pass/fail stripe — one cell per day, oldest left → newest right.
 *  Mirrors GitHub's contribution chart pattern so it's instantly
 *  legible. */
function ComplianceStripe({ stripe }) {
  return (
    <div className="flex gap-1" data-testid="stripe-30d">
      {stripe.map((d) => {
        const color =
          d.total === 0 ? "bg-stone-100" :
          d.pct === 100 ? "bg-eucalyptus-700" :
          d.pct >= 90 ? "bg-eucalyptus-600/60" :
          d.pct >= 75 ? "bg-amber-500/80" :
          "bg-ochre-600";
        const label = d.total === 0
          ? `${d.date} · no checks`
          : `${d.date} · ${d.in_range}/${d.total} (${d.pct}%)`;
        return (
          <div
            key={d.date}
            className={`flex-1 h-8 rounded-sm ${color}`}
            title={label}
            aria-label={label}
          />
        );
      })}
    </div>
  );
}

/** "Today's compliance" headline badge. */
function TodayStatus({ today }) {
  if (today.status === "no_data") {
    return (
      <Badge className="bg-stone-100 text-charcoal-soft hover:bg-stone-100 gap-1" data-testid="today-status">
        <Clock className="w-3.5 h-3.5" /> No checks logged today
      </Badge>
    );
  }
  if (today.status === "pass") {
    return (
      <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100 gap-1" data-testid="today-status">
        <CheckCircle2 className="w-3.5 h-3.5" /> All {today.total_checks} checks in range today
      </Badge>
    );
  }
  return (
    <Badge className="bg-ochre-600 text-white hover:bg-ochre-600 gap-1" data-testid="today-status">
      <AlertTriangle className="w-3.5 h-3.5" /> {today.in_range}/{today.total_checks} in range today
    </Badge>
  );
}

export default function TrustPage() {
  const { siteId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/trust/${encodeURIComponent(siteId)}`);
        if (cancelled) return;
        setState({ loading: false, data, error: "" });
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || err.message || "Trust page not available";
        setState({ loading: false, data: null, error: typeof detail === "string" ? detail : "Trust page not available" });
      }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  // Browser tab title acts as the social-card title when this page is
  // pasted into Slack/Twitter/Google profile. We avoid a full
  // react-helmet dep for one tag.
  useEffect(() => {
    const name = state.data?.site?.name;
    document.title = name ? `${name} · HACCP AU trust report` : "HACCP AU trust report";
    return () => { document.title = "HACCP AU"; };
  }, [state.data]);

  const styles = useMemo(() => gradeStyles(state.data?.trust_score ?? null), [state.data]);

  if (state.loading) {
    return (
      <PublicShell title="Trust report" testid="trust-page">
        <div className="surface p-8 text-center text-charcoal-soft" data-testid="trust-loading">
          Loading compliance data…
        </div>
      </PublicShell>
    );
  }

  if (state.error || !state.data) {
    return (
      <PublicShell title="Trust report" testid="trust-page">
        <div className="surface p-8 text-center" data-testid="trust-error">
          <AlertTriangle className="w-8 h-8 text-ochre-600 mx-auto mb-2" />
          <div className="font-display font-bold text-lg">{state.error || "Trust page not available"}</div>
          <p className="text-sm text-charcoal-soft mt-1">
            This kitchen hasn&apos;t published a public trust page yet, or the link is invalid.
          </p>
        </div>
      </PublicShell>
    );
  }

  const { site, today, month, stripe_30d, last_check_at, trust_score, grade } = state.data;
  const hasAnyData = month.total_checks > 0 || today.total_checks > 0;

  return (
    <PublicShell
      title={site.name}
      subtitle={site.address || (site.type ? site.type.replace("_", " ") : "")}
      badge
      badgeLabel="Live trust report"
      testid="trust-page"
    >
      {/* HERO — gauge + today status side-by-side */}
      <section className="surface p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <TrustGauge score={trust_score} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${styles.chip} hover:${styles.chip} font-display font-bold text-lg px-3 py-1`} data-testid="trust-grade">
                {grade}
              </Badge>
              <TodayStatus today={today} />
            </div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl mt-3 leading-tight">
              {hasAnyData
                ? `${month.in_range_pct ?? 0}% in range over the last 30 days`
                : "Compliance tracking starts today"}
            </h2>
            <p className="text-sm text-charcoal-soft mt-1" data-testid="trust-headline-sub">
              {hasAnyData
                ? `${month.total_checks.toLocaleString()} temperature checks logged · ${month.cleaning_sign_offs} cleaning sign-offs · ${month.training_records} training records`
                : `${site.name} has just joined HACCP AU. Check back tomorrow for live compliance numbers.`}
            </p>
            {last_check_at && (
              <p className="text-xs font-mono text-charcoal-soft mt-3" data-testid="trust-last-check">
                Last check: {fmtDateTime(last_check_at)}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 30-DAY STRIPE */}
      <section className="surface p-6 md:p-8 mb-6" data-testid="trust-30d-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Snowflake className="w-5 h-5 text-eucalyptus-700" />
            <h3 className="font-display font-bold">Last 30 days</h3>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-charcoal-soft">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-eucalyptus-700" /> In-range</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500/80" /> Partial</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-ochre-600" /> Breach</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-stone-100" /> No data</span>
          </div>
        </div>
        <ComplianceStripe stripe={stripe_30d} />
        {!hasAnyData && (
          <p className="text-xs text-charcoal-soft mt-4">
            Every grey cell is a day before this kitchen joined HACCP AU. As soon as the first temperature
            check lands, this stripe lights up.
          </p>
        )}
      </section>

      {/* SECONDARY SIGNALS */}
      <section className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          icon={Snowflake}
          eyebrow="Temperature"
          headline={month.in_range_pct === null ? "—" : `${month.in_range_pct}%`}
          sub={`${month.in_range.toLocaleString()} / ${month.total_checks.toLocaleString()} in range`}
          testid="stat-temp"
        />
        <StatCard
          icon={Sparkles}
          eyebrow="Cleaning"
          headline={month.cleaning_sign_offs.toLocaleString()}
          sub="Sanitation sign-offs (30d)"
          testid="stat-cleaning"
        />
        <StatCard
          icon={GraduationCap}
          eyebrow="Training"
          headline={month.training_records.toLocaleString()}
          sub="Staff records added (30d)"
          testid="stat-training"
        />
      </section>

      {/* TRUST RIBBON — high-contrast dark band; deliberately NOT using
          `.surface` because that class sets bg-white which fights the
          `bg-eucalyptus-900` here (later utility doesn't always win
          across compiled layers). Plain inline styling keeps the
          rendered colours predictable. */}
      <section
        className="rounded-lg border border-eucalyptus-900 p-6 md:p-8 text-center bg-eucalyptus-900"
        data-testid="trust-ribbon"
      >
        <ShieldCheck className="w-8 h-8 mx-auto mb-3" style={{ color: "rgba(255,255,255,0.85)" }} />
        <div className="font-display font-extrabold text-xl" style={{ color: "#ffffff" }}>
          Audit-grade food safety, every shift.
        </div>
        <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "rgba(255,255,255,0.75)" }}>
          {site.name} logs cold-chain temperatures, cleaning sign-offs and staff training every day
          through HACCP AU. Nothing on this page is editable — it&apos;s a live read of the audit ledger.
        </p>
      </section>
    </PublicShell>
  );
}

function StatCard({ icon: Icon, eyebrow, headline, sub, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-eucalyptus-700" />
        <div className="label-eyebrow">{eyebrow}</div>
      </div>
      <div className="font-display font-extrabold text-3xl">{headline}</div>
      <div className="text-xs text-charcoal-soft mt-1">{sub}</div>
    </div>
  );
}
